import React from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {P} from '../../../layout/Text/Text';

import {useTagCheckbox} from './useTagCheckbox';
import {getBaseVariables, getHoverVariables} from './utils';

/**
 * Interface representing the props for the `TagCheckbox` component.
 * This component is designed for selecting or deselecting tags within a form.
 */
export interface ComponentProps {
    /**
     * Override for the active state border color.
     */
    activeBorderColor?: string;
    /**
     * The name of the form field that this checkbox is associated with.
     * This property is used to track the field in the form's data structure.
     */
    fieldName: string;
    /**
     * The value associated with this checkbox.
     * This value will be compared against the form's current value to determine if the checkbox should be checked.
     */
    fieldValue: string;
    /**
     * The current value(s) of the form field.
     * This array contains the selected values for this field, and the component checks if its value is included in this array.
     */
    formValue: string[] | string;
    /**
     * If true, only a single tag can be selected at a time; otherwise, multiple tags can be selected.
     *
     * @default false
     */
    isSingle?: boolean;
    /**
     * The label displayed next to the checkbox.
     * This text should describe the checkbox option, making the UI more user-friendly.
     */
    label: string | undefined;
    /**
     * The callback function that is triggered when the checkbox value changes.
     * This function receives the checkbox value and its checked state as parameters.
     */
    onChange?(value: string[] | string, isChecked: boolean): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'TagCheckbox'
     */
    testId?: string;
}

/**
 * A checkbox component that allows the selection of tags within a form.
 * It displays a label and checks whether the field value is included in the form's current value.
 * When changed, it triggers the `onChange` function with the field value and checked status.
 *
 * @param props                   The component props.
 * @param props.fieldName         The name of the form field associated with this checkbox.
 * @param props.fieldValue        The value associated with this checkbox.
 * @param props.formValue         The current value of the form field.
 * @param props.label             The label displayed next to the checkbox.
 * @param props.activeBorderColor Override for the active state border color.
 * @param props.isSingle          If true, only a single tag can be selected at a time; otherwise, multiple tags can be selected.
 * @param props.onChange          The callback function triggered when the checkbox value changes.
 * @param props.testId            A unique identifier for testing purposes.
 * @returns A checkbox component for selecting or deselecting tags.
 *
 * @example
 * ```tsx
 * <TagCheckbox
 *   fieldName="tag"
 *   fieldValue="exampleTag"
 *   formValue={['exampleTag']}
 *   label="Example Tag"
 *   onChange={(value, checked) => console.log(value, checked)}
 * />
 * ```
 */
const TagCheckbox = ({
    activeBorderColor,
    fieldName,
    fieldValue,
    formValue,
    isSingle = false,
    label,
    onChange = () => {},
    testId = 'TagCheckbox'
}: ComponentProps) => {
    const {handleChange, isActive} = useTagCheckbox(formValue, fieldValue, onChange);

    return (
        <Wrapper
            $activeBorderColor={activeBorderColor}
            data-active={isActive}
            data-cy={testId}
            data-is-single={isSingle}
        >
            <P $size="large" data-cy={`${testId}-label`}>{label}</P>
            <Radio
                checked={isActive}
                name={fieldName}
                type="checkbox"
                value={fieldValue}
                onChange={handleChange}
            />
        </Wrapper>
    );
};

TagCheckbox.displayName = 'TagCheckbox';

export {TagCheckbox};

interface WrapperProps {
    $activeBorderColor?: string;
}

const Wrapper = styled.label<WrapperProps>`
    ${getBaseVariables};
    align-items: center;
    background-color: var(--background-color);
    border: 2px solid var(--border-color);
    border-radius: 4px;
    color: var(--font-color);
    cursor: pointer;
    display: flex;
    justify-content: center;
    padding: ${spacing(1)} ${spacing(3)};
    transition: background-color 0.3s ease-in-out, border-color 0.3s ease-in-out;
    width: max-content;

    &:hover {
        ${getHoverVariables};
    }

    &:has(:focus-visible) {
        ${getBaseVariables};
        outline: 2px solid ${({theme}) => theme.colors.primaryLight};
        outline-offset: 2px;
    }
`;

const Radio = styled.input`
    appearance: none;
    margin: 0;
`;